import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "../../lib/http/axiosConfig";
import { setFarmId } from "../../features/FarmSlice";
import { toast } from "react-toastify";
import { useT } from "../../i18n";

// Sidebar 와 동일한 키를 사용해야 새로고침 후에도 선택한 농장이 유지된다.
const SIDEBAR_FARM_STORAGE_KEY = "tlatfarm:sidebar:activeFarmId";

const saveStoredFarmId = (farmId) => {
  if (farmId == null || farmId === "" || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SIDEBAR_FARM_STORAGE_KEY, String(farmId));
  } catch (error) {
    // sessionStorage 를 못 써도 농장 전환 자체는 동작해야 한다.
  }
};

/**
 * FarmOverlay
 * ------------------------------------------------------------
 * 지도 좌하단에 현재 농장명을 크게 표시하고, 클릭으로 농장을 전환한다.
 * 신규 UI(Calm) 시안의 좌하단 타이틀 영역에 해당한다.
 *
 * - 농장 선택은 기존과 동일하게 Redux setFarmId 로 처리한다.
 * - 미션 수행 중에는 사이드바와 동일하게 전환을 잠근다.
 */
export default function FarmOverlay() {
  const dispatch = useDispatch();
  const t = useT();
  const farmId = useSelector((state) => state.farm.farmId);
  const missionStatus = useSelector((state) => state.drone.control?.missionStatus);
  const missionReturning = useSelector((state) => state.drone.missionReturning);
  const navLocked = Boolean(missionStatus || missionReturning);

  // 농장 목록: Sidebar 가 이미 /api/farms 를 조회해 Redux 에 채워두므로 여기서는
  // 구독만 하고 별도로 재요청하지 않는다 (중복 호출이 전역 rate-limit 을 갉아먹던 문제 수정).
  const farms = useSelector((state) => state.farm.farmList);
  const [location, setLocation] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // 소재지 (농장 목록에는 없고 dashboard info 에만 있다)
  useEffect(() => {
    if (!farmId) {
      setLocation("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`/api/dashboard/${farmId}/info`);
        if (cancelled) return;
        setLocation(data?.data?.farmInfo?.location ?? "");
      } catch (error) {
        if (!cancelled) setLocation("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [farmId]);

  // 바깥 클릭 시 닫기
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const current = farms.find((f) => f.id === farmId);
  if (!current) return null;

  return (
    <div className="calm-place">
      <div className="calm-eyebrow">
        <i />
        {t("common.farmOverlay.eyebrow")}
      </div>
      <h1 className="calm-place-name">{current.name}</h1>
      {location && <div className="calm-place-sub">{location}</div>}

      {farms.length > 1 && (
        <div className="calm-switch-wrap" ref={wrapRef}>
          <button
            type="button"
            className="calm-switch"
            onClick={() => setOpen((v) => !v)}
          >
            {t("common.farmOverlay.switch")}{" "}
            <span>{t("common.farmOverlay.count", { count: farms.length })}</span>
          </button>

          {open && (
            <div className="calm-farmpop">
              {farms.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`it${f.id === farmId ? " sel" : ""}`}
                  onClick={() => {
                    if (navLocked && f.id !== farmId) {
                      toast.warn(t("common.farmOverlay.lockedDuringMission"));
                      return;
                    }
                    saveStoredFarmId(f.id);
                    dispatch(setFarmId(f.id));
                    setOpen(false);
                  }}
                >
                  <span className="it-name">{f.name}</span>
                  <span className="it-ck">✓</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
